import React from 'react';
import { PhoneIcon, CalendarIcon, MapPinIcon, ChevronDownIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage, TRANSLATIONS } from '../../context/LanguageContext';
const CHAIR_IMG = 'https://cdn.magicpatterns.com/uploads/tJeWe6Uo8fYm4gRHVQhCcL/u8896349413_Cinematic_photo_of_a_vintage_barber_chair_in_a_mo_c4ba5aaf-e4f4-4e9e-a189-eaa260c04ed8_0.png';
export function Hero() {
  const {
    language
  } = useLanguage();
  const t = TRANSLATIONS[language || 'en'];
  return <section className="relative w-full" style={{
    minHeight: '100svh'
  }} aria-label="Hero section" data-id="element-87">
      <img src={CHAIR_IMG} alt="Vintage barber chair at Jonathan's Barbershop" className="absolute inset-0 w-full h-full object-cover object-center" fetchPriority="high" decoding="async" data-id="element-88" />

      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/25" aria-hidden="true" data-id="element-89" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-transparent" aria-hidden="true" data-id="element-90" />

      {/* Hero content */}
      <div className="relative z-20 flex flex-col justify-center min-h-[100svh] px-8 md:px-16 lg:px-24 py-24 max-w-4xl" data-id="element-91">
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="text-[#c41e3a] font-semibold tracking-[0.25em] uppercase text-xs md:text-sm mb-4" style={{
        fontFamily: 'Oswald, sans-serif'
      }} data-id="element-92">
          {t.eyebrow}
        </motion.p>

        <motion.h1 initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="text-[2.8rem] md:text-7xl lg:text-8xl font-bold text-white leading-[1.0] mb-5" style={{
        fontFamily: 'Oswald, sans-serif',
        letterSpacing: '0.02em'
      }} data-id="element-93">
          {t.headline_line1}
          <br data-id="element-94" />
          <span className="text-gradient-gold" data-id="element-95">{t.headline_line2}</span>
          <br data-id="element-96" />
          {t.headline_line3}
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.38
      }} className="text-gray-300 text-sm md:text-lg leading-relaxed mb-5 max-w-md" data-id="element-97">
          {t.subtext}
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.45
      }} className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs md:text-sm text-white mb-6 self-start" data-id="element-98">
          <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse flex-shrink-0" aria-hidden="true" data-id="element-99" />
          {t.walkins}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.55
      }} className="flex flex-col sm:flex-row gap-3 mb-6" data-id="element-100">
          <a href="#book" className="flex items-center justify-center gap-2 bg-[#c41e3a] hover:bg-[#a01830] text-white px-7 py-4 rounded font-bold text-sm md:text-base shadow-[0_4px_24px_rgba(196,30,58,0.5)] transition-colors duration-200" aria-label="Book your appointment" style={{
          fontFamily: 'Oswald, sans-serif',
          letterSpacing: '0.1em'
        }} data-id="element-101">
            <CalendarIcon size={17} data-id="element-102" />
            {t.book_cta}
          </a>
          <a href="tel:7864095863" className="flex items-center justify-center gap-2 bg-black/40 hover:bg-black/60 border border-white/25 text-white px-7 py-4 rounded font-bold text-sm md:text-base backdrop-blur-sm transition-colors duration-200" aria-label="Call us" style={{
          fontFamily: 'Oswald, sans-serif',
          letterSpacing: '0.1em'
        }} data-id="element-103">
            <PhoneIcon size={17} data-id="element-104" />
            {t.call_cta}
          </a>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.7
      }} className="flex items-center gap-2 text-gray-400 text-xs md:text-sm" data-id="element-105">
          <MapPinIcon size={12} className="text-[#c41e3a] flex-shrink-0" data-id="element-106" />
          <span data-id="element-107">{t.address}</span>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.4,
      duration: 0.4
    }} className="absolute bottom-5 left-1/2 -translate-x-1/2 z-30 text-gray-500" aria-hidden="true" data-id="element-108">
        <motion.div animate={{
        y: [0, 6, 0]
      }} transition={{
        repeat: Infinity,
        duration: 1.8,
        ease: 'easeInOut'
      }} data-id="element-109">
          <ChevronDownIcon size={22} data-id="element-110" />
        </motion.div>
      </motion.div>
    </section>;
}